import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, UserPlus } from 'lucide-react';
import { T, springs } from '../../lib/tokens';
import { getMembers, cachedMembers } from '../../lib/members';

const AVATAR_COLORS = [
  { bg: T.amberLight,  fg: T.amber  },
  { bg: T.blueLight,   fg: T.blue   },
  { bg: T.violetLight, fg: T.violet },
  { bg: T.greenLight,  fg: T.green  },
  { bg: T.roseLight,   fg: T.rose   },
];

// Stable per-person colour so an avatar looks the same everywhere it appears.
const colorFor = (key = '') => {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

export const displayName = (p) => p?.full_name || p?.email || 'Unknown';

const initials = (p) => {
  const src = (p?.full_name || p?.email || '?').trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
};

export const Avatar = ({ profile, size = 22 }) => {
  const name = displayName(profile);
  if (profile?.avatar_url) {
    return (
      <img src={profile.avatar_url} alt={name} title={name} style={{
        width: size, height: size, borderRadius: '50%',
        objectFit: 'cover', flexShrink: 0, display: 'block',
      }} />
    );
  }
  const c = colorFor(profile?.id || name);
  return (
    <div title={name} style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: c.bg, color: c.fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.42), fontWeight: 700, lineHeight: 1,
    }}>
      {initials(profile)}
    </div>
  );
};

const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
  padding: '6px 8px', borderRadius: 7, border: 'none', background: 'transparent',
  cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: T.text,
};

// Single-select picker for a task's assignee. `current` is the embedded
// profile, used so the button shows the right name before the member list
// (a separate, cached fetch) has resolved.
export const AssigneePicker = ({ value, current, onChange }) => {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState(() => cachedMembers() || []);
  const ref = useRef(null);

  useEffect(() => { getMembers().then(setMembers); }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const selected = members.find(m => m.id === value)
    || (value && current?.id === value ? current : null);

  const pick = (id) => { onChange(id); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 6,
        padding: '3px 7px 3px 4px', borderRadius: 6,
        border: '1px solid rgba(0,0,0,0.10)', background: '#fff',
        cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, color: T.textMid,
      }}>
        {selected ? (
          <>
            <Avatar profile={selected} size={18} />
            <span style={{ maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {displayName(selected)}
            </span>
          </>
        ) : (
          <>
            <div style={{
              width: 18, height: 18, borderRadius: '50%',
              border: '1px dashed rgba(0,0,0,0.25)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <UserPlus size={10} color={T.textMuted} />
            </div>
            <span style={{ color: T.textMuted }}>Assign</span>
          </>
        )}
        <ChevronDown size={11} color={T.textLight} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.97 }}
            transition={springs.snappy}
            style={{
              position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 60,
              width: 220, maxHeight: 264, overflowY: 'auto',
              background: '#fff', borderRadius: 10, boxShadow: T.shadowLg,
              border: '1px solid rgba(0,0,0,0.08)', padding: 4,
            }}
          >
            <button type="button" onClick={() => pick(null)} style={rowStyle}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', border: '1px dashed rgba(0,0,0,0.20)' }} />
              <span style={{ flex: 1, textAlign: 'left', color: T.textMuted }}>Unassigned</span>
              {!value && <Check size={13} color={T.amber} />}
            </button>
            {members.map(m => (
              <button key={m.id} type="button" onClick={() => pick(m.id)} style={rowStyle}>
                <Avatar profile={m} size={22} />
                <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName(m)}
                </span>
                {value === m.id && <Check size={13} color={T.amber} />}
              </button>
            ))}
            {members.length === 0 && (
              <div style={{ padding: 10, fontSize: 11, color: T.textMuted }}>No teammates yet.</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
